import moment from "moment"
import { createSelector, createStructuredSelector } from "reselect"
import { createToJsSelector } from "shared/imports/sharedHelperFunctions"
import beeFreeDefaults from "imports/beeFreeDefaults"
import { parsePlaceholdersForSearchableDropdownButton } from "shared/outbox/selectors"

// this is an obvious selector
const getTemplates = (state) => state.outboxTemplates

/**
 * Creates a simple selectors for email templates
 *
 * @param {String} key string of key to get
 * @returns {Function} returns the select function
 */
export const createTemplateSelector = (key) => createSelector(getTemplates, (templates) => templates.get(key))

// static things
export const getId = createTemplateSelector("id")
export const getName = createSelector(
    getTemplates,
    (templateSlice) => templateSlice.get("name") || `Untitled ${moment().format("MMMM Do, YYYY [at] h:mma")}`,
)
export const getHtmlContent = createTemplateSelector("html_content")
export const getComposeTool = createTemplateSelector("compose_tool")

// moment of the last update to the compose form
export const getLastUpdatedMoment = createTemplateSelector("lastUpdated")
// moment of the last successful autosave of the compose form
export const getLastAutosavedMoment = createTemplateSelector("lastAutosaved")

// immutable json
const getImmutableJsonContent = createTemplateSelector("json_content")
export const getJsonContent = createToJsSelector(getImmutableJsonContent)

// the attachments
const getImmutableAttachments = createTemplateSelector("attachments")
// export const getAttachments = createToJsSelector(getImmutableAttachments)
export const getAttachments = createSelector(getImmutableAttachments, (attachmentList) => {
    const attachmentObjs = []
    attachmentList.forEach((a) => {
        attachmentObjs.push({
            id: a.get("id"),
            name: a.get("name"),
            size: a.get("size"),
        })
    })
    return attachmentObjs
})

// the attachment uris
const attachmentUris = createSelector(getAttachments, (attachmentList) =>
    attachmentList.map((a) => DjangIO.app.userdata.models.Attachment.foreignKey(a.id)),
)

const getImmutablePlaceholders = createTemplateSelector("placeholders")
export const getPlaceholders = createSelector(createToJsSelector(getImmutablePlaceholders), (placeholders) =>
    parsePlaceholdersForSearchableDropdownButton(placeholders),
)

// All templates
export const getTemplateStatus = createTemplateSelector("loadingTemplates")
export const getImmutableTemplates = createTemplateSelector("allTemplates")
const getAllTemplatesDict = createToJsSelector(getImmutableTemplates)

export const getAllTemplates = createSelector(getAllTemplatesDict, (templates) =>
    Object.values(templates).sort((a, b) => (moment(b.updated).isAfter(a.updated) ? 1 : -1)),
)

export const getSummernoteTemplates = createSelector(getAllTemplates, (templates) =>
    templates.length === 0
        ? []
        : [
              {
                  label: "Your Templates",
                  options: templates
                      .filter((t) => t.compose_tool === "summernote")
                      .map((template) => ({
                          label: template.name,
                          value: template.id,
                          ...template,
                      })),
              },
          ],
)

export const getDefaultTemplates = () => [
    {
        label: "Default Templates",
        options: beeFreeDefaults.map((template) => ({
            label: template.name,
            value: template.slug,
            ...template,
        })),
    },
]

export const getBeeFreeTemplates = createSelector(
    getAllTemplates,
    getDefaultTemplates,
    (templates, defaultTemplates) => {
        // Return early if there are no templates
        if (templates.length === 0) {
            return defaultTemplates
        }

        const yourTemplates =
            templates.length === 0
                ? []
                : [
                      {
                          label: "Your Templates",
                          options: templates
                              .filter((t) => t.compose_tool === "beefree")
                              .map((template) => ({
                                  label: template.name,
                                  value: template.id,
                                  ...template,
                              })),
                      },
                  ]

        return yourTemplates.concat(defaultTemplates)
    },
)

// bee plugin instance
const getBeeImmutable = createTemplateSelector("beePluginInstance")
export const getBeePlugin = createToJsSelector(getBeeImmutable)

// status things
const isGetting = createTemplateSelector("fetching")
const isCreating = createTemplateSelector("posting")
const isPatching = createTemplateSelector("patching")
export const requestFailed = createTemplateSelector("requestFailed")
export const isLoading = createSelector(isGetting, isCreating, (getting, creating) => !!(getting || creating))

export const isUpdating = createSelector(isLoading, isPatching, (loading, patching) => !!(loading || patching))

export const selectEmailTemplateLimitedToTeams = createToJsSelector(createTemplateSelector("limited_to_teams"))

// patch selector
export const patchTemplateSelector = createStructuredSelector({
    attachments: attachmentUris,
    compose_tool: getComposeTool,
    html_content: getHtmlContent,
    json_content: getJsonContent,
    limited_to_teams: selectEmailTemplateLimitedToTeams,
    name: getName,
})
